import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { stringify } from "qs";

import useAuth from "src/hooks/useAuth";
import type { Id } from "src/types/id";
import { Query } from "src/types/query";
import { UserRoleEnum } from "src/types/role";
import type { User, UserPreferenceKey, UserRelationIds } from "src/types/user";
import axios from "src/utils/axios";
import { filterUsersToDisplay, filterUsersToDisplayWithCount } from "src/utils/shouldDisplayEntity";

export const useQueryUsers = (
    enabled = true,
    organizationId: number,
    light?: boolean
): UseQueryResult<UserRelationIds[], unknown> => {
    const { user } = useAuth();
    const displayHiddenEntities = user?.role === UserRoleEnum.SUPERADMIN;
    return useQuery({
        queryKey: [Query.OrganizationUsers, user?.id, organizationId],

        queryFn: async () =>
            filterUsersToDisplayWithCount(
                (
                    await axios.get<{ users: UserRelationIds[] }>(
                        `/user${stringify({ organizationId, light }, { addQueryPrefix: true })}`
                    )
                ).data,
                displayHiddenEntities
            ),

        enabled,
        placeholderData: light ? keepPreviousData : undefined,
        staleTime: 5 * 60 * 1000,
    });
};

export const useQueryUsersWithCount = (
    enabled = true,
    organizationId: number,
    light?: boolean,
    params?: {
        page: number;
        limit: number;
        order: string;
        orderBy: string;
    }
): UseQueryResult<
    {
        users: UserRelationIds[];
        count: number;
    },
    unknown
> => {
    const { page, limit, order, orderBy } = params || { page: 0, limit: 10, order: "ASC" };
    const { user } = useAuth();
    const displayHiddenEntities = user?.role === UserRoleEnum.SUPERADMIN;
    return useQuery({
        queryKey: [Query.OrganizationUsers, user?.id, page, limit, order, orderBy],

        queryFn: async () =>
            filterUsersToDisplay(
                (
                    await axios.get<{ users: UserRelationIds[]; count: number }>(
                        `/user${stringify({ organizationId, light }, { addQueryPrefix: true })}`,
                        {
                            params: { page, limit, order, orderBy },
                        }
                    )
                ).data,
                displayHiddenEntities
            ),

        enabled,
        placeholderData: light ? keepPreviousData : undefined,
        staleTime: 5 * 60 * 1000,
    });
};

export const useQueryUser = (userId: Id, withSelect: boolean): UseQueryResult<User, unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.User, user.id, userId],

        queryFn: async () =>
            (
                await axios.get<{ user: User }>(
                    `/user/${userId}${stringify({ withSelect }, { addQueryPrefix: true })}`
                )
            ).data.user,
    });
};

export const useQueryUserRefinerSignature = (
    userId?: Id
): UseQueryResult<{ signature: string | null }, unknown> =>
    useQuery({
        queryKey: [Query.User, "refinerSignature", userId],
        queryFn: async () =>
            (await axios.get<{ signature: string | null }>(`/user/${userId}/refiner/signature`))
                .data,
        enabled: !!userId,
    });

export function queryUserPreferences(userId: number): Promise<
    AxiosResponse<
        {
            preferences: UserPreferenceKey[];
        },
        any
    >
> {
    return axios.get<{ preferences: UserPreferenceKey[] }>(`/user/${userId}/preferences`);
}

export function useQueryUserPreferences(
    userIdProp?: number
): UseQueryResult<{ preferences: UserPreferenceKey[] }, unknown> {
    const { user } = useAuth();

    const userId = userIdProp ?? user.id;

    return useQuery({
        queryKey: [Query.UserPreferences, user.id, userId],
        queryFn: async () => (await queryUserPreferences(userId)).data,
    });
}
